'use client';

import { Calendar, Globe, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  EN_DAYS_OF_WEEK,
  formatDateShort,
  minutesToTime,
  utcMinutesToLocalDisplay,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Separator,
} from '@virtual-care/shadcn';
import { useData } from '@virtual-care/ui';

interface DoctorAvailabilityDialogProps {
  doctorId: string;
  doctorName: string;
  timezone?: string | null;
  trigger: ReactNode;
}

function formatSlotTime(minutes: number, timezone?: string | null): string {
  if (timezone) {
    return utcMinutesToLocalDisplay(minutes, timezone);
  }
  return minutesToTime(minutes);
}

export function DoctorAvailabilityDialog({
  doctorId,
  doctorName,
  timezone,
  trigger,
}: DoctorAvailabilityDialogProps) {
  const { data, isLoading } = useData('GET /availability/doctor/{doctorId}', {
    doctorId,
  });

  const availability = data?.availability || [];
  const overrides = data?.overrides || [];

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="size-5" />
            Doctor Availability - {doctorName}
          </DialogTitle>
          {timezone && (
            <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
              <Globe className="size-3.5" />
              {timezone}
            </div>
          )}
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground flex items-center gap-3">
                <div className="border-primary size-6 animate-spin rounded-full border-b-2"></div>
                <span className="text-sm font-medium">
                  Loading doctor availability...
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="text-primary size-5" />
                    <h3 className="text-lg font-semibold">Weekly Schedule</h3>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {availability.filter((day) => day.slots.length > 0).length}/
                    {EN_DAYS_OF_WEEK.length} days active
                  </Badge>
                </div>

                {!availability || availability.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                      <Calendar className="text-muted-foreground/50 mx-auto mb-3 size-12" />
                      <div className="text-muted-foreground mb-1 font-medium">
                        No Availability Schedule
                      </div>
                      <div className="text-muted-foreground text-sm">
                        This doctor hasn't set up their weekly availability yet.
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent>
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-7">
                        {EN_DAYS_OF_WEEK.map((dayName) => {
                          const dayUpperCase =
                            dayName.toUpperCase() as Uppercase<typeof dayName>;
                          const dayAvailability = availability.find(
                            (item) => item.weekday === dayUpperCase,
                          );

                          return (
                            <div key={dayName} className="space-y-2">
                              <div className="flex items-center justify-between lg:flex-col lg:items-start">
                                <h4 className="text-muted-foreground text-sm font-semibold tracking-wide capitalize">
                                  {dayName.slice(0, 3)}
                                </h4>
                                <Badge
                                  variant={
                                    dayAvailability &&
                                    dayAvailability.slots.length > 0
                                      ? 'default'
                                      : 'secondary'
                                  }
                                  className="flex w-full justify-center text-xs lg:mt-1"
                                >
                                  {!dayAvailability ||
                                  dayAvailability.slots.length === 0
                                    ? 'Closed'
                                    : `${dayAvailability.slots.length} slots`}
                                </Badge>
                              </div>

                              <div className="space-y-1">
                                {dayAvailability &&
                                dayAvailability.slots.length > 0 ? (
                                  dayAvailability.slots.map(
                                    (slot, slotIndex) => (
                                      <div
                                        key={slotIndex}
                                        className="bg-primary/5 border-primary/20 rounded border px-2 py-1 font-mono text-xs"
                                      >
                                        {formatSlotTime(
                                          slot.startTime,
                                          timezone,
                                        )}
                                        -
                                        {formatSlotTime(slot.endTime, timezone)}
                                      </div>
                                    ),
                                  )
                                ) : (
                                  <div className="text-muted-foreground/50 flex h-8 items-center justify-center">
                                    <XCircle className="h-4 w-4" />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="text-destructive h-5 w-5" />
                    <h3 className="text-lg font-semibold">Date Overrides</h3>
                  </div>
                  {overrides.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {overrides.length} override
                      {overrides.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>

                {overrides.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-6 text-center">
                      <div className="mb-1 font-medium text-green-600">
                        No Date Overrides
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Doctor follows their weekly schedule with no
                        date-specific changes.
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {overrides.map((override) => {
                      const dateStr =
                        typeof override.date === 'string'
                          ? override.date.split('T')[0]
                          : override.date;
                      const overrideDate = new Date(dateStr + 'T00:00:00');
                      const isBlocked = override.slots.length === 0;

                      return (
                        <Card
                          key={override.id}
                          className={`border-l-4 ${
                            isBlocked
                              ? 'border-l-destructive bg-destructive/5'
                              : 'border-l-primary bg-primary/5'
                          }`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant={
                                      isBlocked ? 'destructive' : 'default'
                                    }
                                    className="text-xs"
                                  >
                                    {isBlocked ? 'Blocked' : 'Custom Schedule'}
                                  </Badge>
                                  <span className="text-sm font-medium">
                                    {formatDateShort(overrideDate)}
                                  </span>
                                </div>
                                {!isBlocked && (
                                  <div className="flex flex-wrap gap-1">
                                    {override.slots.map((slot, i) => (
                                      <span
                                        key={i}
                                        className="bg-primary/10 border-primary/20 rounded border px-2 py-0.5 font-mono text-xs"
                                      >
                                        {formatSlotTime(
                                          slot.startTime,
                                          timezone,
                                        )}
                                        -
                                        {formatSlotTime(slot.endTime, timezone)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end border-t pt-4">
          <DialogTrigger asChild>
            <Button variant="outline" className="px-6">
              Close
            </Button>
          </DialogTrigger>
        </div>
      </DialogContent>
    </Dialog>
  );
}
